@if (chat.available()) {
  <div class="study-chat">
    <button
      type="button"
      class="study-chat__fab"
      [attr.aria-expanded]="chat.panelOpen()"
      aria-label="Study Team Chat"
      (click)="chat.togglePanel()"
    >
      <!-- Chat bubble with message dots (vertically centered in viewBox). -->
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <mask id="chat-icon-dots-fab">
            <rect width="24" height="24" fill="#fff" />
            <circle cx="9" cy="11.25" r="1.15" fill="#000" />
            <circle cx="12" cy="11.25" r="1.15" fill="#000" />
            <circle cx="15" cy="11.25" r="1.15" fill="#000" />
          </mask>
        </defs>
        <path
          fill="currentColor"
          mask="url(#chat-icon-dots-fab)"
          d="M6 4.75h12A2.75 2.75 0 0 1 20.75 7.5v7A2.75 2.75 0 0 1 18 17.25h-4.2L10.3 20.5a.7.7 0 0 1-1.15-.55v-2.7H6A2.75 2.75 0 0 1 3.25 14.5v-7A2.75 2.75 0 0 1 6 4.75Z"
        />
      </svg>
      @if (unreadLabel()) {
        <span class="study-chat__fab-badge">{{ unreadLabel() }}</span>
      }
      <span class="study-chat__tooltip">Study Team Chat</span>
    </button>

    @if (chat.panelOpen()) {
      <section class="study-chat__panel" aria-label="Study team chat">
        @if (chat.view() === 'list') {
          <div class="study-chat__list">
            <header class="study-chat__list-header">
              <div class="study-chat__title">
                <span class="study-chat__title-icon" aria-hidden="true">
                  <!-- Same chat-bubble icon as the floating button. -->
                  <svg viewBox="0 0 24 24">
                    <defs>
                      <mask id="chat-icon-dots-title">
                        <rect width="24" height="24" fill="#fff" />
                        <circle cx="9" cy="11.25" r="1.15" fill="#000" />
                        <circle cx="12" cy="11.25" r="1.15" fill="#000" />
                        <circle cx="15" cy="11.25" r="1.15" fill="#000" />
                      </mask>
                    </defs>
                    <path
                      fill="currentColor"
                      mask="url(#chat-icon-dots-title)"
                      d="M6 4.75h12A2.75 2.75 0 0 1 20.75 7.5v7A2.75 2.75 0 0 1 18 17.25h-4.2L10.3 20.5a.7.7 0 0 1-1.15-.55v-2.7H6A2.75 2.75 0 0 1 3.25 14.5v-7A2.75 2.75 0 0 1 6 4.75Z"
                    />
                  </svg>
                </span>
                <h2>Chat</h2>
              </div>
              <button type="button" class="study-chat__icon-btn" aria-label="Close chat" (click)="chat.closePanel()">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 18L18 6M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </button>
            </header>

            @if (chat.isSuperAdmin()) {
              <div class="study-chat__tabs" role="tablist">
                <button
                  type="button"
                  class="study-chat__tab"
                  role="tab"
                  [class.study-chat__tab--on]="chat.adminTab() === 'employees'"
                  [attr.aria-selected]="chat.adminTab() === 'employees'"
                  (click)="chat.setAdminTab('employees')"
                >
                  Employees
                </button>
                <button
                  type="button"
                  class="study-chat__tab"
                  role="tab"
                  [class.study-chat__tab--on]="chat.adminTab() === 'interactions'"
                  [attr.aria-selected]="chat.adminTab() === 'interactions'"
                  (click)="chat.setAdminTab('interactions')"
                >
                  Conversations
                </button>
              </div>
            }

            <div class="study-chat__search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <input
                type="text"
                [value]="chat.search()"
                placeholder="Search by name..."
                aria-label="Search by name"
                (input)="onSearch($event)"
              />
            </div>

            <div class="study-chat__contacts">
              @if (chat.isSuperAdmin() && chat.adminTab() === 'interactions') {
                @if (chat.loadingInteractions() && chat.interactions().length === 0) {
                  <p class="study-chat__empty">Loading conversations...</p>
                } @else if (chat.filteredInteractions().length === 0) {
                  <p class="study-chat__empty">
                    {{ chat.search() ? 'No conversations match that name.' : 'No conversations on this study.' }}
                  </p>
                } @else {
                  @for (row of chat.filteredInteractions(); track row.firstUniqueUserId + row.secondUniqueUserId) {
                    <button
                      type="button"
                      class="study-chat__contact"
                      [attr.title]="pairLabel(row.firstFirstName, row.firstLastName, row.secondFirstName, row.secondLastName)"
                      (click)="chat.openInteraction(row)"
                    >
                      <span class="study-chat__contact-body">
                        <span class="study-chat__contact-row">
                          <span
                            class="study-chat__contact-name study-chat__pair-line"
                            [attr.title]="pairLabel(row.firstFirstName, row.firstLastName, row.secondFirstName, row.secondLastName)"
                          >
                            {{ pairLabel(row.firstFirstName, row.firstLastName, row.secondFirstName, row.secondLastName) }}
                          </span>
                          <span class="study-chat__time">{{ stamp(row.lastMessageDate) }}</span>
                        </span>
                        <span class="study-chat__preview">
                          {{ row.lastMessageIsDeleted ? 'Deleted: ' + (row.lastMessage || '') : row.lastMessage || 'No messages yet' }}
                        </span>
                      </span>
                    </button>
                  }
                }
              } @else if (chat.loadingContacts() && chat.contacts().length === 0) {
                <p class="study-chat__empty">Loading teammates...</p>
              } @else if (chat.filteredContacts().length === 0) {
                <p class="study-chat__empty">
                  {{ chat.search() ? 'No teammates match that name.' : 'No teammates on this study.' }}
                </p>
              } @else {
                @for (contact of chat.filteredContacts(); track contact.uniqueUserId) {
                  <button
                    type="button"
                    class="study-chat__contact"
                    [class.study-chat__contact--active]="contact.uniqueUserId === chat.activeContactId()"
                    (click)="openContact(contact.uniqueUserId)"
                  >
                    @if (contact.photo) {
                      <img class="study-chat__avatar study-chat__avatar-img" [src]="contact.photo" alt="" (error)="hidePhoto(contact)" />
                    } @else {
                      <span class="study-chat__avatar" [style.background]="avatarColor(contact.firstName, contact.lastName)">
                        {{ initials(contact.firstName, contact.lastName) }}
                      </span>
                    }
                    <span class="study-chat__contact-body">
                      <span class="study-chat__contact-row">
                        <span class="study-chat__contact-name">{{ name(contact.firstName, contact.lastName) }}</span>
                        <span class="study-chat__time">{{ stamp(contact.lastMessageDate) }}</span>
                      </span>
                      <span class="study-chat__status">
                        <span
                          class="study-chat__dot"
                          [class.study-chat__dot--online]="chat.onlineIds().has(contact.uniqueUserId)"
                        ></span>
                        <span>{{ chat.onlineIds().has(contact.uniqueUserId) ? 'Online' : 'Offline' }}</span>
                      </span>
                      <span class="study-chat__contact-row">
                        <span class="study-chat__preview">{{ contact.lastMessage || 'No messages yet' }}</span>
                        @if (contact.unreadCount > 0) {
                          <span class="study-chat__unread">{{ contact.unreadCount > 99 ? '99+' : contact.unreadCount }}</span>
                        }
                      </span>
                    </span>
                  </button>
                }
              }
            </div>
          </div>
        } @else if (chat.interaction(); as pair) {
          <div class="study-chat__thread">
            <header class="study-chat__thread-header">
              <div class="study-chat__thread-person">
                <button type="button" class="study-chat__icon-btn" aria-label="Back to conversations" (click)="chat.backToList()">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M15 19l-7-7 7-7"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <span
                  class="study-chat__pair-title"
                  [attr.title]="pairLabel(pair.firstFirstName, pair.firstLastName, pair.secondFirstName, pair.secondLastName)"
                >
                  {{ pairLabel(pair.firstFirstName, pair.firstLastName, pair.secondFirstName, pair.secondLastName) }}
                </span>
              </div>
              <button type="button" class="study-chat__icon-btn" aria-label="Close chat" (click)="chat.closePanel()">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 18L18 6M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </button>
              </header>
            <div class="study-chat__deleted-bar">
              <button
                type="button"
                class="study-chat__deleted-btn"
                [class.study-chat__deleted-btn--on]="chat.showDeleted()"
                [attr.aria-pressed]="chat.showDeleted()"
                (click)="chat.toggleDeleted()"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>{{ chat.showDeleted() ? 'Hide deleted' : 'Show deleted' }}</span>
              </button>
            </div>

            <div class="study-chat__messages" #messagesEl>
              @if (chat.hasOlder()) {
                <button type="button" class="study-chat__earlier" [disabled]="chat.loadingOlder()" (click)="loadOlder()">
                  {{ chat.loadingOlder() ? 'Loading...' : 'Load earlier messages' }}
                </button>
              }
              @if (chat.loadingMessages() && chat.messages().length === 0) {
                <p class="study-chat__empty">Loading messages...</p>
              } @else if (threadEntries().length === 0) {
                <p class="study-chat__empty">No messages yet.</p>
              } @else {
                @for (entry of threadEntries(); track entry.trackId) {
                  @if (entry.kind === 'day') {
                    <div class="study-chat__day"><span>{{ entry.label }}</span></div>
                  } @else if (entry.message) {
                    <div class="study-chat__message study-chat__message--right">
                      <div class="study-chat__bubble-row study-chat__bubble-row--mine">
                        <span class="study-chat__sender">{{ name(entry.message.senderFirstName, entry.message.senderLastName) }}</span>
                        <div
                          class="study-chat__bubble"
                          [class.study-chat__bubble--mine]="chat.isInteractionRight(entry.message) && !entry.message.isDeleted"
                          [class.study-chat__bubble--deleted]="entry.message.isDeleted"
                        >
                          {{ entry.message.messageText }}
                        </div>
                        <div class="study-chat__meta">
                          <span>{{ messageTime(entry.message) }}</span>
                          @if (entry.message.isDeleted) {
                            <span>Deleted</span>
                          }
                        </div>
                      </div>
                    </div>
                  }
                }
              }
            </div>
          </div>
        } @else {
          @if (chat.activeContact(); as contact) {
            <div class="study-chat__thread">
              <header class="study-chat__thread-header">
                <div class="study-chat__thread-person">
                  <button type="button" class="study-chat__icon-btn" aria-label="Back to contacts" (click)="chat.backToList()">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M15 19l-7-7 7-7"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  @if (contact.photo) {
                    <img class="study-chat__avatar study-chat__avatar--sm study-chat__avatar-img" [src]="contact.photo" alt="" (error)="hidePhoto(contact)" />
                  } @else {
                    <span class="study-chat__avatar study-chat__avatar--sm" [style.background]="avatarColor(contact.firstName, contact.lastName)">
                      {{ initials(contact.firstName, contact.lastName) }}
                    </span>
                  }
                  <span>
                    <span class="study-chat__contact-name">{{ name(contact.firstName, contact.lastName) }}</span>
                    <span class="study-chat__status">
                      <span class="study-chat__dot" [class.study-chat__dot--online]="chat.onlineIds().has(contact.uniqueUserId)"></span>
                      <span>{{ chat.onlineIds().has(contact.uniqueUserId) ? 'Online' : 'Offline' }}</span>
                    </span>
                  </span>
                </div>
                <button type="button" class="study-chat__icon-btn" aria-label="Close chat" (click)="chat.closePanel()">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 18L18 6M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </button>
              </header>

              <div class="study-chat__messages" #messagesEl [class.study-chat__messages--selecting]="selectedCount() > 0">
                @if (chat.hasOlder()) {
                  <button type="button" class="study-chat__earlier" [disabled]="chat.loadingOlder()" (click)="loadOlder()">
                    {{ chat.loadingOlder() ? 'Loading...' : 'Load earlier messages' }}
                  </button>
                }
                @if (chat.loadingMessages() && chat.messages().length === 0) {
                  <p class="study-chat__empty">Loading messages...</p>
                } @else if (threadEntries().length === 0) {
                  <p class="study-chat__empty">No messages yet.</p>
                } @else {
                  @for (entry of threadEntries(); track entry.trackId) {
                    @if (entry.kind === 'day') {
                      <div class="study-chat__day"><span>{{ entry.label }}</span></div>
                    } @else if (entry.message) {
                      <div
                        class="study-chat__message"
                        [class.study-chat__message--mine]="chat.isMine(entry.message)"
                      >
                        @if (selectedCount() > 0 && chat.isMine(entry.message)) {
                          <button
                            type="button"
                            class="study-chat__tick"
                            [class.study-chat__tick--on]="isSelected(entry.message)"
                            [attr.aria-pressed]="isSelected(entry.message)"
                            [attr.aria-label]="isSelected(entry.message) ? 'Deselect message' : 'Select message'"
                            (click)="toggleSelect(entry.message)"
                          >
                            @if (isSelected(entry.message)) {
                              <svg viewBox="0 0 20 20" aria-hidden="true">
                                <path
                                  fill="currentColor"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                />
                              </svg>
                            }
                          </button>
                        }
                        <div
                          class="study-chat__bubble-row"
                          [class.study-chat__bubble-row--mine]="chat.isMine(entry.message)"
                          (click)="onBubbleClick(entry.message)"
                        >
                          <div class="study-chat__bubble" [class.study-chat__bubble--mine]="chat.isMine(entry.message)">
                            {{ entry.message.messageText }}
                          </div>
                          <div class="study-chat__meta">
                            <span>{{ messageTime(entry.message) }}</span>
                            @if (chat.isMine(entry.message)) {
                              <svg viewBox="0 0 20 20" aria-hidden="true" [attr.title]="entry.message.readDate ? 'Read' : 'Sent'">
                                <path
                                  fill="currentColor"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                />
                              </svg>
                            }
                          </div>
                        </div>
                      </div>
                    }
                  }
                }
              </div>

              @if (selectedCount() > 0) {
                <div class="study-chat__select-bar">
                  <button
                    type="button"
                    class="study-chat__select-delete"
                    aria-label="Delete selected messages"
                    [disabled]="deleting()"
                    (click)="askDelete()"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <span>{{ selectedCount() }} Selected</span>
                </div>
              } @else {
                <form class="study-chat__composer" (submit)="send($event)">
                  @if (chat.sendError()) {
                    <p class="study-chat__error">{{ chat.sendError() }}</p>
                  }
                  <div class="study-chat__composer-row">
                    <input
                      type="text"
                      name="message"
                      maxlength="2000"
                      autocomplete="off"
                      placeholder="Type a message..."
                      aria-label="Type a message"
                      [value]="draft()"
                      (input)="onDraft($event)"
                    />
                    <button type="submit" class="study-chat__send" aria-label="Send message" [disabled]="chat.sending() || !draft().trim()">
                      <svg viewBox="0 0 20 20" aria-hidden="true">
                        <path
                          fill="currentColor"
                          d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
                        />
                      </svg>
                    </button>
                  </div>
                </form>
              }

              @if (confirming() && selectedCount() > 0) {
                <div class="study-chat__confirm" role="dialog" aria-modal="true" [attr.aria-label]="deletePrompt()">
                  <div class="study-chat__confirm-card">
                    <p>{{ deletePrompt() }}</p>
                    <div class="study-chat__confirm-actions">
                      <button type="button" class="study-chat__confirm-cancel" [disabled]="deleting()" (click)="cancelDelete()">
                        Cancel
                      </button>
                      <button type="button" class="study-chat__confirm-ok" [disabled]="deleting()" (click)="confirmDelete()">
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        }
      </section>
    }
  </div>
}
